/* global Highcharts:false */
/* global loadHighcharts:false */
/* global addScrollIntoViewListener:false */

/**
 * @param {HTMLElement} domElement
 * @param {Object[]} bars
 * @param {string} bars[].color
 * @param {string} [bars[].description = '{label}: {value}%']
 * @param {string} bars[].label
 * @param {number} bars[].size
 * @param {Object} [options]
 * @param {boolean} [options.animated = false]
 * @param {string} [options.caption]
 * @param {boolean} [options.dark = false]
 * @param {string} [options.description]
 * @param {number} [options.height = 400]
 * @param {string} [options.title]
 * @param {number} [options.width]
 */
function createBarChart(
	domElement,
	bars,
	options = {}
) {
	const {
		animated = true,
		caption,
		dark = false,
		description,
		height = 400,
		title,
		width,
	} = options;

	const biggestBar = Math.max(...bars.map(bar => bar.size))
	let ticks = [];

	if (biggestBar > 70) {
		ticks = [0, 20, 40, 60, 80, 100]
	} else if (biggestBar > 45) {
		ticks = [0, 20, 40, 60, 80]
	} else if (biggestBar > 30) {
		ticks = [0, 10, 20, 30, 40, 50]
	} else if (biggestBar > 20) {
		ticks = [0, 5, 10, 15, 20, 25, 30, 35]
	} else {
		ticks = [0, 5, 10, 15, 20]
	}

	let lastLabelWidth;

	const config = {
		accessibility: {
			description,
			point: {
				descriptionFormatter: (point) => {
					const values = {
						value: point.options.y,
						label: point.category,
					};

					const template = bars.description || '{label}: {value}%';

					return template.replace(/{([^{}]+)}/g, (_, key) => {
						return values.hasOwnProperty(key) ? values[key] : '{key}';
					}).replace(/<br\/>/g, ' ');
				},
			},
			screenReaderSection: {
				beforeChartFormat: `
					<div>{typeDescription}</div>
					<div>{chartLongdesc}</div>
				`,
			},
			series: {
				descriptionFormatter: () => {
					return 'Bars';
				},
			},
			typeDescription: `Bar chart with ${bars.length} bars.`,
		},
		caption: {
			align: 'center',
			style: {
				color: dark ? '#fff' : '#12181f',
				fontSize: '14px',
				fontWeight: 400,
				lineHeight: '28px',
			},
			text: caption,
			verticalAlign: 'bottom',
			useHTML: true,
		},
		chart: {
			backgroundColor: dark ? '#3f0f45' : '#f5f7f9',
			events: {
				render: function() {
					const labelWidth = this.chartWidth > 550
						? 130
						: this.plotWidth - 40;

					if (lastLabelWidth !== labelWidth) {
						lastLabelWidth = labelWidth;
						this.update({
							xAxis: {
								labels: {
									style : {
										width: labelWidth,
									},
								},
							},
						}, true, false, false);
					}

					if (this.chartWidth <= 550) {
						const barHeight = this.series[0].barW;
						this.xAxis[0].labelGroup.translate(0, (barHeight / -2) - 8);
					}
				},
			},
			height,
			spacingBottom: 20,
			spacingLeft: 20,
			spacingRight: 60,
			spacingTop: 23,
			type: 'bar',
			width,
		},
		credits: {
			enabled: false,
		},
		legend: {
			enabled: false,
		},
		plotOptions: {
			bar: {
				borderWidth: 0,
				grouping: false,
			},
			series: {
				dataLabels: {
					align: 'left',
					animation: animated ? {
						defer: 1000,
					} : false,
					color: dark ? '#fff' : '#12181f',
					enabled: true,
					format: '{point.y:0f}%',
					style: {
						textOutline: false,
					},
				},
				states: {
					inactive: {
						opacity: 1,
					},
				},
			},
		},
		responsive: {
			rules: [
				{
					chartOptions: {
						chart: {
							spacingRight: 20,
						},
						plotOptions: {
							bar: {
								pointPadding: 0.25,
							},
						},
						xAxis: {
							labels: {
								align: 'left',
								reserveSpace: false,
								style: {
									left: '24px',
									overflow: 'hidden',
									right: '24px',
									textAlign: 'left',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								},
								x: 4,
								y: 0,
							},
							width: 40,
						},
					},
					condition: {
						maxWidth: 550,
					},
				},
			],
		},
		series: [
			{
				accessibility: {
					enabled: false,
				},
				animation: false,
				color: dark ? 'rgb(255 255 255 / 10%)' : 'rgb(0 0 0 / 5%)',
				data: bars.map(() => 100),
				dataLabels: {
					enabled: false,
				},
				states: {
					hover: {
						enabled: false,
					},
				},
				yAxis: 1,
			},
			{
				animation: animated ? {
					defer: 500,
					easing: (x) => 1 - Math.pow(1 - x, 3),
					duration: 1000,
				} : false,
				data: bars.map(bar => ({
					accessibility: {
						description: `${bar.label}: ${bar.size} percent`,
					},
					color: bar.color,
					y: bar.size,
				})),
			},
		],
		title: {
			margin: 27,
			style: {
				color: dark ? '#fff' : '#12181f',
				fontSize: '20px',
				fontWeight: 400,
				lineHeight: '20px',
			},
			text: title,
		},
		tooltip: {
			enabled: false,
		},
		xAxis: {
			accessibility: {
				enabled: false,
			},
			categories: bars.map(bar => bar.label),
			labels: {
				style: {
					color: dark ? '#fff' : '#12181f',
					fontSize: '12px',
					lineHeight: '14px',
					paddingRight: '0px',
					right: 0,
					textAlign: 'right',
					width: 130,
				},
				useHTML: false,
				x: -10,
			},
			lineWidth: 0,
			width: 200,
		},
		yAxis: [
			{
				accessibility: {
					enabled: false,
					rangeDescription: 'hello',
				},
				gridLineColor: dark ? '#6A3F74' : '#ddd',
				gridLineWidth: 2,
				labels: {
					align: 'center',
					format: '{value:0f}%',
					overflow: 'allow',
					style: {
						color: dark ? '#fff' : '#12181f',
						fontSize: '13px',
						fontWeight: 400,
						lineHeight: '28px',
					},
				},
				min: 0,
				tickPositions: ticks,
				title: {
					text: null,
				},
			},
			{
				accessibility: {
					enabled: false,
					rangeDescription: 'hello',
				},
				linkedTo: 0,
				visible: false,
			},
		],
	};

	let isInView = false;
	let isHighchartsReady = false;

	function render() {
		if (isInView && isHighchartsReady) {
			Highcharts.chart(domElement, config);
		}
	}

	addScrollIntoViewListener(domElement, () => {
		isInView = true;
		render();
	});

	loadHighcharts(() => {
		isHighchartsReady = true;
		render();
	});
}
